import { useAgentUI } from 'components/agent/agent-panel-wrapper';
import { Sheet, SheetContent, SheetTitle } from 'design-system/sheet/sheet';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { routes } from 'routes/routes';

import { NavBar } from './navbar';
import { SettingsSidebarContainer } from './settings-sidebar-container';
import { useSidebarUI } from './sidebar-context';

type DrawerView = 'main' | 'settings';

/**
 * Single, app-level mobile nav drawer. Mounted once (not per page) so it
 * survives route changes: closing on navigation animates cleanly instead of
 * tearing the dialog down mid-close (which left the body pointer-lock stuck).
 * Below lg the inline sidebar is a rail; this holds the full sidebar on expand.
 *
 * Settings and Back swap the drawer view in place instead of navigating:
 * navigation closes the drawer, which would force a reopen just to pick the
 * actual destination.
 */
export const MobileNavDrawer = () => {
  const { isMobileOpen, closeMobile } = useSidebarUI();
  const { isOpen: isAgentOpen } = useAgentUI();
  const { pathname } = useLocation();

  const settingsPath = routes.settings.build();
  const routeView: DrawerView =
    pathname === settingsPath || pathname.startsWith(`${settingsPath}/`)
      ? 'settings'
      : 'main';

  // Derived during render so a stale view never paints; the override clears
  // once navigation or a close invalidates it.
  const [override, setOverride] = useState<DrawerView | null>(null);
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [prevOpen, setPrevOpen] = useState(isMobileOpen);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setOverride(null);
  }
  if (isMobileOpen !== prevOpen) {
    setPrevOpen(isMobileOpen);
    setOverride(null);
  }
  const view = override ?? routeView;

  const contentRef = useRef<HTMLDivElement>(null);

  // The activated control unmounts on swap, so move focus into the entering view.
  useEffect(() => {
    if (override !== null) {
      contentRef.current?.querySelector<HTMLElement>('button, a')?.focus();
    }
  }, [override]);

  // Opening the agent panel doesn't change the route, so the drawer's
  // navigation-based close can't catch it. Keying off the agent's open state
  // closes the drawer from every agent entry point; a per-handler close would
  // miss the ones outside this tree.
  useEffect(() => {
    if (isAgentOpen) {
      closeMobile();
    }
  }, [isAgentOpen, closeMobile]);

  return (
    <Sheet
      open={isMobileOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeMobile();
        }
      }}
    >
      <SheetContent
        side="left"
        showCloseButton={false}
        overlayClassName="z-[200]"
        className="bg-background data-[side=left]:w-sidebar z-[200] gap-0 p-0 data-[side=left]:border-r-0"
      >
        <SheetTitle className="sr-only">
          {view === 'settings' ? 'Settings navigation' : 'Navigation'}
        </SheetTitle>
        <div ref={contentRef} className="h-full">
          {view === 'settings' ? (
            <SettingsSidebarContainer
              inDrawer
              onBack={() => setOverride('main')}
            />
          ) : (
            <NavBar inDrawer onSettingsSelect={() => setOverride('settings')} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
MobileNavDrawer.displayName = 'MobileNavDrawer';
